import { useQuery } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { ListTable, Notice, Plate, type ListColumn } from "@wilco/ui";
import { useState } from "react";
import { formatNumber, t } from "../i18n.ts";
import { ScanBar } from "../scan/ScanBar.tsx";
import { stockApi } from "./api.ts";
import { productQuery } from "./queries.ts";
import { errorNotice, type NoticeState } from "./ShelfPage.tsx";
import type { ProductStock, WarehouseStock } from "./types.ts";

const route = getRouteApi("/app/stock/product/$sku");

/**
 * Товар по скану артикула: полки на «Основному», остатки складов
 * деятельности и «Списати» оттуда (INV-26). С «Основного» списывается
 * только недостача подсчёта, поэтому его в выборе нет (H2).
 */
export function ProductPage() {
  const { sku } = route.useParams();
  const navigate = useNavigate();
  const product = useQuery(productQuery(sku));
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const scan = async (code: string) => {
    setNotice(null);
    try {
      const r = await stockApi.scan(code);
      if (r.kind === "shelf") await navigate({ to: "/stock/shelf", search: { code: r.code } });
      else await navigate({ to: "/stock/product/$sku", params: { sku: r.sku } });
    } catch (e) {
      setNotice(errorNotice(e));
    }
  };

  const p = product.data;
  return (
    <div className="page stock-narrow">
      <h1 className="page-title">{t("product.title")}</h1>
      <ScanBar onScan={(c) => void scan(c)} placeholder={t("shelf.scan")} />
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {product.error ? <Notice tone="danger" title={errorNotice(product.error).title} className="stock-notice" /> : null}
      {p ? (
        <>
          <Plate key={p.product.sku} cut="md" bordered className="shelf-card w-build-in">
            <div className="shelf-card-head">
              <span className="shelf-code w-num">{p.product.sku}</span>
            </div>
            <div className="shelf-product">{p.product.name}</div>
            {p.shelves.length > 0 ? (
              <ul className="product-shelves" aria-label={t("product.shelves")}>
                {p.shelves.map((s) => (
                  <li key={s.code}>
                    <Link to="/stock/shelf" search={{ code: s.code }} className="product-shelf">
                      <span className="w-num">{s.code}</span>
                      {s.onShelf !== null ? <span className="w-num">{`${formatNumber(s.onShelf)}\u00a0${p.product.uom}`}</span> : null}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </Plate>
          <section className="stock-section">
            <h2 className="desk-group-title">{t("product.stock")}</h2>
            <StockTable stock={p} />
          </section>
          <WriteOff />
        </>
      ) : null}
    </div>
  );
}

function StockTable({ stock }: { stock: ProductStock }) {
  const uom = stock.product.uom;
  const columns: ListColumn<WarehouseStock>[] = [
    { key: "warehouse", header: t("product.warehouse"), kind: "title", render: (r) => r.warehouse.name },
    { key: "onHand", header: t("product.onHand"), kind: "number", render: (r) => `${formatNumber(r.onHand)}\u00a0${uom}` },
    { key: "reserved", header: t("product.reserved"), kind: "number", render: (r) => `${formatNumber(r.reserved)}\u00a0${uom}` },
    { key: "free", header: t("product.free"), kind: "number", render: (r) => `${formatNumber(r.onHand - r.reserved)}\u00a0${uom}` },
  ];
  return <ListTable label={t("product.stock")} columns={columns} rows={stock.stock} rowKey={(r) => r.warehouse.code} />;
}

/**
 * «Списати» со склада деятельности (INV-26) в контракте склада пока нет:
 * форма вернётся вместе с REST. Здесь только объяснение, чтобы кнопку не искали.
 */
function WriteOff() {
  return (
    <section className="stock-section" aria-labelledby="write-off">
      <h2 id="write-off" className="desk-group-title">
        {t("writeOff.title")}
      </h2>
      <p className="w-muted">{t("writeOff.notInApi")}</p>
    </section>
  );
}
